import type { ConfigCamadaImagem, MedidasPagina, Paleta } from '@miolos/core';
import { glifoDoIcone, idDoIcone, resolverCamada, ehIcone } from '@miolos/core';

interface Props {
  camada: ConfigCamadaImagem;
  medidas: MedidasPagina;
  /** Página verso (par): só aqui o espelhamento na lombada se aplica. */
  verso: boolean;
  temaEscuro: boolean;
  paleta: Paleta;
  resolverImagem?: (url: string) => string;
  /** Modo de edição: mostra a moldura da camada para facilitar o ajuste. */
  destacar?: boolean;
}

function posicaoDoRecorte(camada: ConfigCamadaImagem): string {
  switch (camada.ajuste) {
    case 'TOPO':
      return 'center top';
    case 'ESQUERDA':
      return 'left center';
    default:
      return 'center center';
  }
}

/**
 * Uma camada de imagem (fundo, faixa de borda, canto ou figura solta).
 *
 * A posição vem resolvida do core, em milímetros: aqui só desenhamos. O véu é
 * aplicado como uma camada separada da imagem, para não perder a força quando
 * a imagem está com opacidade baixa.
 */
export function CamadaImagem({
  camada,
  medidas,
  verso,
  temaEscuro,
  paleta,
  resolverImagem,
  destacar = false,
}: Props) {
  const resolvida = resolverCamada(camada, medidas, camada.espelharPares && verso, temaEscuro);
  const icone = ehIcone(camada.url);
  const glifo = icone ? glifoDoIcone(idDoIcone(camada.url)) : '';
  const url = resolverImagem ? resolverImagem(camada.url) : camada.url;

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: `${resolvida.esquerdaMm}mm`,
        top: `${resolvida.topoMm}mm`,
        width: `${resolvida.larguraMm}mm`,
        height: `${resolvida.alturaMm}mm`,
        transform: resolvida.rotacaoGraus ? `rotate(${resolvida.rotacaoGraus}deg)` : undefined,
        transformOrigin: 'center center',
        zIndex: camada.ordem + 1,
        overflow: 'hidden',
        outline: destacar ? `0.4mm dashed ${paleta.destaque}` : undefined,
        outlineOffset: destacar ? '-0.2mm' : undefined,
      }}
    >
      {icone ? (
        // Ícone da biblioteca: SVG desenhado aqui, então sai nítido em qualquer
        // tamanho e muda de cor sem precisar de novo arquivo.
        <svg
          viewBox="0 0 24 24"
          width="100%"
          height="100%"
          fill="none"
          stroke={camada.cor ?? paleta.destaque}
          strokeWidth={Math.max(1.1, 3.4 - (camada.larguraMm ?? 20) / 14)}
          strokeLinecap="round"
          strokeLinejoin="round"
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: 'block',
            opacity: camada.opacidade,
            filter: camada.desfoque > 0 ? `blur(${camada.desfoque}px)` : undefined,
          }}
          dangerouslySetInnerHTML={{ __html: glifo }}
        />
      ) : (
        <img
          src={url}
          alt=""
          draggable={false}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: camada.ajuste === 'CONTER' ? 'contain' : 'cover',
            objectPosition: posicaoDoRecorte(camada),
            opacity: camada.opacidade,
            filter: camada.desfoque > 0 ? `blur(${camada.desfoque}px)` : undefined,
          }}
        />
      )}
      {camada.veu > 0 ? (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            background: temaEscuro ? paleta.principalEscuro : paleta.fundo,
            opacity: camada.veu,
          }}
        />
      ) : null}
    </div>
  );
}
